import {
  faChevronRight,
  faDollar,
  faGift,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { OfferBundleLabel, usePricingModel } from './pricing.model';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { InputPrice } from '@design-system/components/input/inputPrice';
import { Collection } from '../collection.domain';
import { Tag } from '@design-system/components/tag/tag';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { Alert } from '@design-system/components/alert/alert';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import * as styles from './ProgramPricing.css';

type ProgramPricingProps = {
  creatorId: number;
  collection: Collection;
  isModified: boolean;
  save: () => Promise<boolean>;
};

const BundleItem = ({
  bundle,
  onClick,
}: {
  bundle: OfferBundleLabel;
  onClick: () => void;
}) => (
  <button type="button" className={styles.bundleLabel} onClick={onClick}>
    <div className={styles.bundleInfo}>
      <Typography
        as="span"
        variant="body"
        size="medium"
        fontWeight="medium"
        color="N-100"
        className={styles.bundleName}
      >
        {bundle.name}
      </Typography>
      <Typography
        as="span"
        variant="body"
        size="small"
        fontWeight="normal"
        color="N-80"
        className={styles.contentDescription}
      >
        {bundle.contentDescription}
      </Typography>
    </div>
    <div className={styles.bundleMeta}>
      <Tag
        label={bundle.status.label}
        readonly
        className={clsx(
          styles.statusTag,
          sprinkles({
            background: bundle.status.color.bg,
            color: bundle.status.color.text,
          }),
        )}
      />
      {bundle.isFree && (
        <Tag
          label="Free"
          startIcon={faGift}
          readonly
          className={clsx(
            styles.freeTag,
            sprinkles({
              background: { lightMode: 'P-20', hover: 'P-20' },
            }),
          )}
        />
      )}
      {bundle.price && (
        <Typography
          as="span"
          variant="body"
          size="medium"
          fontWeight="medium"
          color="N-100"
          className={styles.bundlePrice}
        >
          {bundle.price}
        </Typography>
      )}
    </div>
    <FontAwesomeIcon icon={faChevronRight} className={styles.chevron} />
  </button>
);

export const ProgramPricing = ({
  creatorId,
  collection,
  isModified,
  save,
}: ProgramPricingProps) => {
  const model = usePricingModel({ creatorId, collection, isModified, save });

  return (
    <AsyncRenderer
      data={model}
      renderer={(data) => (
        <div className={styles.container}>
          <UnsavedChangesModal {...data.unsavedChangesModal} />
          <div className={styles.card}>
            {data.offers.length === 0 ? (
              <div className={styles.cardHeader}>
                <div className={styles.sectionText}>
                  <Typography variant="body" size="large">
                    Offers
                  </Typography>
                  <Typography
                    variant="body"
                    size="medium"
                    fontWeight="normal"
                    color="N-80"
                  >
                    Add to an Offer to adjust your landing page, set a price,
                    and more.
                  </Typography>
                </div>
                <Button
                  color="primary"
                  variant="text"
                  className={styles.offersButton}
                  onClick={data.addToOffer}
                >
                  Add to Offer
                </Button>
              </div>
            ) : (
              <>
                <div className={styles.sectionText}>
                  <Typography variant="body" size="large">
                    This program is included in an offer already
                  </Typography>
                  <Typography
                    variant="body"
                    size="medium"
                    fontWeight="normal"
                    color="N-80"
                  >
                    Click the offer below and modify the price, landing page,
                    and more.
                  </Typography>
                </div>
                <div className={styles.bundleList}>
                  {data.offers.map((offer, index) => (
                    <BundleItem
                      key={`${offer.id}-${index}`}
                      bundle={offer}
                      onClick={() => data.openOffer(offer.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {data.currentPrice !== null && (
            <div className={styles.card}>
              <InputPrice
                label="Price"
                required
                disabled
                value={data.currentPrice / 100}
                onChange={() => undefined}
                startIcon={{ icon: faDollar }}
                wrapperClassName="lg:w-[150px]"
              />
              <Alert
                variant="info"
                customComponent={
                  <Typography variant="body" size="medium" fontWeight="normal">
                    To change its price you need to create a new OTP Offer. Read
                    this{' '}
                    <a
                      href="https://help.playbookapp.io/en/articles/9655526-one-time-purchase-strategy-pricing-and-packaging"
                      target="_blank"
                      rel="noreferrer"
                      className={styles.helpArticleLink}
                    >
                      help article
                    </a>{' '}
                    on how to change price.
                  </Typography>
                }
              />
            </div>
          )}
        </div>
      )}
    />
  );
};
